import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Printer } from 'lucide-react';
import { BACKEND_URL } from '@/config';
import { DateLabel, EmptyState, PageHeader } from '@/components/design';
import { money } from '@/components/SenderFeeSummary';
import '@/styles/receipt.css';

const API = `${BACKEND_URL}/api`;

/**
 * Reçu de paiement de l'expéditeur (C8, partie 3b) : le transport, encaissé pour le compte du transporteur, puis les
 * frais Colis2Ship et le total — tel que le serveur le construit (`GET /missions/{id}/receipt`). Imprimable en PDF
 * par le navigateur. Mentions de démonstration (hors production) signalées.
 */
export default function ReceiptPage() {
  const { id } = useParams();
  const [receipt, setReceipt] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    axios.get(`${API}/missions/${id}/receipt`)
      .then((r) => setReceipt(r.data))
      .catch((e) => setError(e?.response?.data?.detail || 'Reçu indisponible.'));
  }, [id]);

  const back = (
    <Link to={`/app/missions/${id}`} className="rcp-back" data-testid="receipt-back">
      <ArrowLeft size={16} aria-hidden="true" /> Retour à la mission
    </Link>
  );

  if (error) {
    return <div className="rcp-page">{back}<EmptyState title="Pas de reçu">{error}</EmptyState></div>;
  }
  if (!receipt) return <div className="rcp-page" aria-busy="true" />;

  const ccy = receipt.currency;
  const offert = !(receipt.sender_fee > 0) && receipt.sender_fee_listed > 0;
  const { legal } = receipt;
  return (
    <div className="rcp-page">
      {back}
      <PageHeader
        title={receipt.refunded ? 'Reçu — remboursé' : 'Reçu de paiement'}
        subtitle={<>N° {receipt.number} · payé le <DateLabel value={receipt.paid_at} /></>}
        actions={(
          <button type="button" className="btn btn-outline" onClick={() => window.print()} data-testid="receipt-print">
            <Printer size={16} aria-hidden="true" /> Imprimer ou enregistrer en PDF
          </button>
        )}
      />

      {legal.demo && (
        <p className="rcp-demo" data-testid="receipt-demo">
          Mentions de démonstration : ce reçu n&apos;a pas de valeur légale.
        </p>
      )}

      <section className="rcp-card" data-testid="receipt">
        <div className="rcp-route">{receipt.route}</div>
        <table className="rcp-lines">
          <tbody>
            <tr>
              <th scope="row">
                Transport par {receipt.carrier_name}
                <span className="rcp-hint">encaissé par Colis2Ship pour son compte</span>
              </th>
              <td data-testid="receipt-transport">{money(receipt.transport, ccy)}</td>
            </tr>
            {receipt.sender_fee_listed > 0 && (
              <tr>
                <th scope="row">Frais de service Colis2Ship</th>
                <td data-testid="receipt-fee">
                  {offert ? <><s>{money(receipt.sender_fee_listed, ccy)}</s> offerts</> : money(receipt.sender_fee, ccy)}
                </td>
              </tr>
            )}
            <tr className="rcp-total">
              <th scope="row">{receipt.refunded ? 'Remboursé' : 'Total payé'}</th>
              <td data-testid="receipt-total">{money(receipt.total, ccy)}</td>
            </tr>
          </tbody>
        </table>
        {receipt.refunded && receipt.refunded_at && (
          <p className="rcp-refund">Remboursé le <DateLabel value={receipt.refunded_at} />, frais compris.</p>
        )}
      </section>

      <footer className="rcp-legal" data-testid="receipt-legal">
        <div>{legal.name} · {legal.address} · SIRET {legal.siret}</div>
        <div>{legal.vat_mention}</div>
      </footer>
    </div>
  );
}
